import { useMemo } from 'react'
import type { CalendarEvent } from '@highlight/common'
import { useLinkedAudioNotes } from '@renderer/hooks/audio-notes'
import { CELL_CONTEXT_MENU_TRIGGER, CellActionsCluster, CellActionsMenu, type CellAction } from '../cell-actions'
import { HOVER_CLUSTER_TRIGGER } from '../cell-hover-buttons'
import { useJoinMeeting } from './use-join-meeting'
import {
  findSummaryReadyNote,
  isJoinWindowOpen,
  meetingCellActions,
  useOpenMeetingSummary,
} from './meeting-quick-actions'

// Every control here is a quick action — the trigger carries both the surface
// and the entry point, matching MeetingCell's `sidepanel-hover-cluster`.
const ROW_CLUSTER_TRIGGER = `meeting-row-${HOVER_CLUSTER_TRIGGER}`
const ROW_MENU_TRIGGER = `meeting-row-${CELL_CONTEXT_MENU_TRIGGER}`

/**
 * The flat meeting row's moves: View Summary and Join, sharing
 * `meeting-quick-actions`' gates with the lead cell.
 *
 * Its own hook, called only once the row has actually been reached, because the
 * join machinery and the linked-notes query are exactly the subscriptions
 * `MeetingRowView` documents keeping off its render path — every remaining
 * meeting today renders one of these rows.
 */
function useMeetingRowCellActions(meeting: CalendarEvent, trigger: string): Array<CellAction> {
  const { joinLink, joinLabel, isAwaitingJoin, handleJoinClick } = useJoinMeeting(meeting, trigger)
  const linkedAudioNotes = useLinkedAudioNotes(meeting)
  const openSummary = useOpenMeetingSummary(meeting)

  // Unlike MeetingCell's `canJoin`, no recording gates: those only arbitrate
  // against the Transcribe CTAs, which the flat row never hosts.
  const canJoin = !!joinLink && isJoinWindowOpen(meeting)
  const summaryAudioNote = useMemo(() => findSummaryReadyNote(linkedAudioNotes, meeting), [linkedAudioNotes, meeting])

  return meetingCellActions({
    showSummary: !!summaryAudioNote,
    onViewSummary: () => {
      if (summaryAudioNote) openSummary(summaryAudioNote.id, trigger)
    },
    showJoin: canJoin,
    joinLabel,
    isAwaitingJoin,
    onJoin: handleJoinClick,
  })
}

interface MeetingRowActionsProps {
  meeting: CalendarEvent
  /** The row's own open, forced into a window — the row owns that routing (brief vs plain). */
  onPopout: () => void
}

/**
 * The row's hover cluster. Mounted only once the pointer has reached the row —
 * see `useMeetingRowCellActions`.
 */
export function MeetingRowHoverButtons({ meeting, onPopout }: MeetingRowActionsProps) {
  const actions = useMeetingRowCellActions(meeting, ROW_CLUSTER_TRIGGER)
  return <CellActionsCluster actions={actions} onPopout={onPopout} />
}

/** The row's right-click menu. Mounted only while the menu is open. */
export function MeetingRowContextMenuItems({ meeting, onPopout }: MeetingRowActionsProps) {
  const actions = useMeetingRowCellActions(meeting, ROW_MENU_TRIGGER)
  return <CellActionsMenu actions={actions} onPopout={onPopout} />
}
